import { useMemo } from 'react';
import {
  TableCell,
  TableBody,
  TableRow,
  TableHead,
  Table,
  CircularProgress,
  Box,
} from '@mui/material';
import type { BoxProps } from '@mui/material/Box';
import { alpha, styled } from '@mui/material/styles';
import FullPageSuccess from 'components/FullPageSuccess';
import UploadStatus from 'components/UploadStatus';
import { Margin } from 'components/Layout';
import { InvestorInvestmentUploadStatus } from 'further-types/investor-investment-upload';
import api from 'lib/trpcClient';
import { CalendarDay } from 'further-ui/utils';
import type { UploadStatusData } from './types';

const NoteText = styled(Box)(({ theme }) => ({
  fontSize: theme.fontSizes?.lg ?? 16,
  color: theme.palette.text.darkBodyText ?? '#656565',
}));

const ScrollableTable = styled('div')(({ theme }) => ({
  overflow: 'auto',
  padding: theme.spacing(2.5, 0),
  margin: theme.spacing(0, 0, 6),
  clear: 'both',
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.grey[400], 0.2),
}));

const StyledHeadCell = styled(TableCell)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    minWidth: 120,
  },
  borderBottomColor: theme.palette.borderColor?.lightGrey ?? '#EDEDED',
  lineHeight: '22px',
  fontSize: theme.fontSizes?.sm ?? 12,
  fontWeight: 600,
  fontStyle: 'normal',
}));

const StyledBodyCell = styled(TableCell)(({ theme }) => ({
  borderBottomColor: theme.palette.borderColor?.lightGrey ?? '#EDEDED',
}));

const StyledCircularProgress = styled(CircularProgress)(({ theme }) => ({
  marginLeft: theme.spacing(2),
}));

const InlineStatusRoot: React.ComponentType<BoxProps> = styled(Box)(
  ({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  }),
);

const InlineStatus: React.FC<BoxProps> = (props) => (
  <InlineStatusRoot component="span" {...props} />
);

type Props = {
  uploadId: string;
  firmId: string;
  initialStatus?: UploadStatusData;
  onRestart: () => void;
};

const getIdentityDateLabel = (identityDate?: CalendarDay | string) => {
  if (!identityDate) return '';
  if (
    typeof identityDate !== 'string' &&
    typeof identityDate.toReadable === 'function'
  ) {
    return identityDate.toReadable();
  }
  return String(identityDate);
};

const UploadStatusV2: React.FC<Props> = ({
  uploadId,
  firmId,
  initialStatus,
  onRestart,
}) => {
  const getUploadStatusInput = useMemo(
    () => ({ uploadId, firmId }),
    [uploadId, firmId],
  );
  const { data, isFetching, isError, error } =
    api.investorInvestmentUpload.getUploadStatus.useQuery(
      getUploadStatusInput,
      {
        enabled: Boolean(uploadId) && Boolean(firmId),
        refetchInterval: (query) => {
          const status = query.state.data?.status;
          const keepPolling =
            !query.state.data ||
            status === InvestorInvestmentUploadStatus.Pending ||
            status === InvestorInvestmentUploadStatus.Processing;
          return keepPolling ? 2000 : false;
        },
        refetchOnWindowFocus: false,
      },
    );

  const hasResolvedData = Boolean(data ?? initialStatus);
  const isLoadingFromScratch = !hasResolvedData && isFetching;
  const isLoadError = !hasResolvedData && !isFetching && isError;

  const status = data?.status ?? initialStatus?.status;
  const failedRows = data?.failedRows ?? [];
  const hasFailures =
    (data?.failureCount ?? initialStatus?.failureCount ?? 0) > 0;

  const isUploadInProgress =
    status === InvestorInvestmentUploadStatus.Pending ||
    status === InvestorInvestmentUploadStatus.Processing;

  const statusDisplay = useMemo(() => {
    const label = status ?? (isFetching ? 'Loading' : '');
    if (isUploadInProgress) {
      return (
        <InlineStatus>
          <span>{label}</span>
          <StyledCircularProgress size={18} />
        </InlineStatus>
      );
    }
    return label;
  }, [status, isFetching, isUploadInProgress]);

  const summaryRows = useMemo(
    () => [
      { label: 'Status', value: statusDisplay },
      {
        label: 'Total rows',
        value: data?.totalRows ?? initialStatus?.totalRows ?? 0,
      },
      {
        label: 'Succeeded',
        value: data?.successCount ?? initialStatus?.successCount ?? 0,
      },
      {
        label: 'Failed',
        value: data?.failureCount ?? initialStatus?.failureCount ?? 0,
      },
    ],
    [data, initialStatus, statusDisplay],
  );

  if (isLoadingFromScratch) {
    return (
      <UploadStatus
        title="Upload status"
        summaryRows={[
          {
            label: 'Status',
            value: (
              <InlineStatus>
                <span>Loading upload status...</span>
                <StyledCircularProgress size={18} />
              </InlineStatus>
            ),
          },
        ]}
        onRestart={onRestart}
        restartButtonText="Create another upload"
      />
    );
  }

  if (isLoadError) {
    return (
      <UploadStatus
        title="Upload status"
        summaryRows={[
          {
            label: 'Status',
            value: error?.message ?? 'Unable to load upload status.',
          },
        ]}
        onRestart={onRestart}
        restartButtonText="Back to upload"
      />
    );
  }

  if (status === InvestorInvestmentUploadStatus.Completed && !hasFailures) {
    return (
      <FullPageSuccess
        title="Upload completed"
        additionalText="All investors and subscriptions have been processed successfully."
        proceedText="Create another upload"
        proceedCallback={onRestart}
      />
    );
  }

  const failedRowsContent =
    failedRows.length > 0 ? (
      <>
        <Margin bottom="lg">
          <NoteText>
            Some rows failed to process. Review the details below.
          </NoteText>
        </Margin>
        <ScrollableTable>
          <Table>
            <StyledTableHead>
              <TableRow>
                <StyledHeadCell>Row number</StyledHeadCell>
                <StyledHeadCell>Name</StyledHeadCell>
                <StyledHeadCell>Identity date</StyledHeadCell>
                <StyledHeadCell>Email</StyledHeadCell>
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {failedRows.map((row) => (
                <TableRow key={row.rowId}>
                  <StyledBodyCell>{row.rowId + 1}</StyledBodyCell>
                  <StyledBodyCell>
                    {row.snapshot?.displayName ?? ''}
                  </StyledBodyCell>
                  <StyledBodyCell>
                    {getIdentityDateLabel(row.snapshot?.identityDate)}
                  </StyledBodyCell>
                  <StyledBodyCell>{row.snapshot?.email ?? ''}</StyledBodyCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollableTable>
      </>
    ) : undefined;

  return (
    <UploadStatus
      title="Upload status"
      summaryRows={summaryRows}
      failedRowsContent={failedRowsContent}
      onRestart={onRestart}
      restartButtonText="Create another upload"
    />
  );
};

export default UploadStatusV2;
